import React, { useEffect, useState, Component, ErrorInfo, ReactNode } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { logError, logInfo, logWarn } from './utils/logger';

// Clear old authentication data on app start to fix JWT issues
const clearOldAuthData = () => {
  const token = localStorage.getItem('token');
  if (token) {
    // Check if token looks like it might be old (basic check)
    try {
      const tokenParts = token.split('.');
      if (tokenParts.length === 3) {
        const payload = JSON.parse(atob(tokenParts[1]));
        const tokenAge = Date.now() - (payload.iat * 1000);
        const oneDay = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
        
        // If token is older than 1 day, clear it
        if (tokenAge > oneDay) {
          console.log('🧹 Clearing old authentication data to fix JWT issues');
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          localStorage.removeItem('refreshToken');
          return true;
        }
      }
    } catch (error) {
      // If we can't parse the token, it might be corrupted, so clear it
      console.log('🧹 Clearing corrupted authentication data');
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('refreshToken');
      return true;
    }
  }
  return false;
};

import { FinanceBotV2Section } from './sections/FinanceBotV2Section';
import { AllocationsSection } from './sections/AllocationsSection';
import { LoginForm } from './components/Auth/LoginForm';
import { ChangePasswordForm } from './components/Auth/ChangePasswordForm';
import LoginCallback from './pages/LoginCallback';
import SapCallback from './pages/SapCallback';
import Sidebar from './components/Layout/Sidebar';
import { Header } from './components/Layout/Header';
import { NotificationsSection } from './sections/NotificationsSection';
import UserManagementPage from './pages/UserManagementPage';
import './styles/colors.css';

import { AlertTriangle } from 'lucide-react';
import { SettingsSection } from './sections/SettingsSection';
import { EvaluationsSection } from './sections/EvaluationsSection';
import SapIntelligencePage from './pages/SapIntelligencePage';
import SacDashboardPage from './pages/SacDashboardPage';
import LaborPerformancePage from './pages/LaborPerformancePage';
import AdoptionDashboardPage from './pages/AdoptionDashboardPage';
import RedzoneDowntimeAnalysisPage from './pages/RedzoneDowntimeAnalysisPage';
import PreCannedReportRenderPage from './pages/PreCannedReportRenderPage';
import PreCannedReportsPage from './pages/PreCannedReportsPage';

import { PlatformLoader } from './components/Loading/LoadingComponents';
import { ToastProvider } from './components/UI/toaster';
import { PRE_CANNED_REPORTS_ENABLED } from './config/featureFlags';

// Error Boundary Component
class ErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean; error?: Error }> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    logError('App', 'ErrorBoundary caught an error', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-[#FAF7F1] flex items-center justify-center p-4">
          <div className="bg-white border border-[#EDE6E1] rounded-xl shadow-sm p-8 max-w-md w-full">
            <div className="flex items-center gap-3 mb-4">
              <AlertTriangle className="w-8 h-8 text-[#B91C1C]" />
              <h1 className="text-xl font-semibold text-[#1F1A20]">Something went wrong</h1>
            </div>
            <p className="text-sm text-[#1F1A20] mb-5 leading-relaxed">
              The application encountered an error. Please try refreshing the page.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="w-full inline-flex items-center justify-center rounded-lg bg-[#6E3450] px-4 py-2 text-sm font-medium text-white hover:bg-[#5A2A41] transition-colors"
            >
              Refresh Page
            </button>
            {this.state.error && (
              <details className="mt-4">
                <summary className="text-sm text-[#7B6B72] cursor-pointer hover:text-[#1F1A20]">
                  Error Details
                </summary>
                <pre className="text-xs text-[#B91C1C] mt-2 p-2 bg-red-50 rounded border border-red-200 overflow-auto">
                  {this.state.error.toString()}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isLoading: authLoading } = useAuth();

  // Wait for auth check to complete
  if (authLoading) {
    return <PlatformLoader />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// Admin Protected Route Component
const AdminProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isLoading: authLoading, isAdmin } = useAuth();

  if (authLoading) {
    return <PlatformLoader />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!isAdmin()) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

// Module → URL path map. Shared by:
//   - the root redirect (`/` → first accessible)
//   - per-route gating (<ModuleProtectedRoute>)
// Order matters: it determines which module a user lands on by default.
// Add a row here when a new module ships.
// Keep this order aligned with the visible sidebar so:
//   - the SmartCHG / CHG logo click (which navigates to ``/`` and is
//     resolved by ``RootRedirect``)
//   - the post-login default landing
//   - the bounce target when a user pastes a URL they cannot access
// all resolve to the FIRST sidebar-visible route the user has
// permission for.
//
// Hidden modules intentionally do NOT live here — their routes are still
// mounted in ``<Routes>`` below and gated by ``<ModuleProtectedRoute>``,
// but they are never a default-landing target.
const SMART_INSIGHTS_ROUTE_PATH = '/smart-insights';
const ALLOCATIONS_ROUTE_PATH = '/allocations';
const SMART_DASHBOARDS_ROUTE_PATH = '/smart-dashboards';
const SMART_MORNING_ROUTE_PATH = '/smart-morning';
const SMART_MORNING_LEADERS_ROUTE_PATH = '/smart-morning-leaders';
const PRE_CANNED_REPORTS_ROUTE_PATH = '/smart-morning-for-leaders';
const LEGACY_PRE_CANNED_REPORTS_ROUTE_PATH = '/pre-canned-reports';
const LABOR_PERFORMANCE_ROUTE_PATH = '/labor-performance';
const REDZONE_ANALYSIS_ROUTE_PATH = '/redzone-analysis';
const SMART_FINANCE_ROUTE_PATH = '/smart-finance';
const ENABLE_ALLOCATION_ACCESS_RESTRICTION = true;

const MODULE_ROUTES: ReadonlyArray<{ slug: string; path: string; isPublic?: boolean }> = [
  ...(PRE_CANNED_REPORTS_ENABLED
    ? [{ slug: 'smart_morning_finance', path: PRE_CANNED_REPORTS_ROUTE_PATH, isPublic: true }]
    : []),
  { slug: 'labor_performance', path: LABOR_PERFORMANCE_ROUTE_PATH, isPublic: true },
  { slug: 'sac_smart_morning', path: SMART_MORNING_ROUTE_PATH },
  { slug: 'sac_wow_dashboard', path: SMART_DASHBOARDS_ROUTE_PATH },
  { slug: 'smart_insights_bot', path: SMART_INSIGHTS_ROUTE_PATH },
  { slug: 'redzone_analysis', path: REDZONE_ANALYSIS_ROUTE_PATH },
  { slug: 'smart_finance', path: SMART_FINANCE_ROUTE_PATH },
  { slug: 'allocation', path: ALLOCATIONS_ROUTE_PATH, isPublic: !ENABLE_ALLOCATION_ACCESS_RESTRICTION },
  { slug: 'settings', path: '/settings' },
];

/** First module path the user can access, or null if none. */
const useFirstAccessibleRoute = (): string | null => {
  const { canAccessModule, isAdmin } = useAuth();
  for (const r of MODULE_ROUTES) {
    if (r.slug === 'sac_smart_morning' && !isAdmin()) continue;
    if (r.isPublic || canAccessModule(r.slug)) return r.path;
    if (r.slug === 'sac_wow_dashboard' && canAccessModule('smart_insights_bot')) {
      return r.path;
    }
  }
  return null;
};

const NoModuleAccess: React.FC = () => (
  <div className="flex min-h-full items-center justify-center bg-neutral-50 p-6">
    <div className="w-full max-w-md rounded-xl border border-neutral-200 bg-white p-8 text-center shadow-sm">
      <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-error-50">
        <AlertTriangle className="h-6 w-6 text-error-600" />
      </div>
      <h1 className="text-xl font-semibold text-neutral-950">Access Denied</h1>
      <p className="mt-3 text-sm leading-6 text-neutral-600">
        You do not have permission to access this module. Please contact your administrator to request access.
      </p>
    </div>
  </div>
);

// Generic module gate. Wrap every module route so a paste-the-URL attempt
// never reaches a screen the user can't access.
//   - Not authed → /login
//   - No permission → bounce to the first module the user CAN access, or show
//     an access-denied state if the user has no module permissions.
const ModuleProtectedRoute: React.FC<{ children: React.ReactNode; moduleSlug: string }> = ({ children, moduleSlug }) => {
  const { user, isLoading: authLoading, canAccessModule, isAdmin } = useAuth();
  const fallback = useFirstAccessibleRoute();
  const isSuperAdminOnlyModule = moduleSlug === 'sac_smart_morning';
  const isSacFinanceModule = moduleSlug === 'sac_wow_dashboard';
  const hasModuleAccess =
    (isSuperAdminOnlyModule && isAdmin()) ||
    (!isSuperAdminOnlyModule &&
      (canAccessModule(moduleSlug) || (isSacFinanceModule && canAccessModule('smart_insights_bot'))));

  if (authLoading) return <PlatformLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (!hasModuleAccess) {
    return fallback ? <Navigate to={fallback} replace /> : <NoModuleAccess />;
  }
  return <>{children}</>;
};

/** Root path (`/`) lands on the first module the user can access. */
const RootRedirect: React.FC = () => {
  const fallback = useFirstAccessibleRoute();
  return fallback ? <Navigate to={fallback} replace /> : <NoModuleAccess />;
};

const LegacyPreCannedReportRenderRedirect: React.FC = () => {
  const { templateId = '' } = useParams();
  const location = useLocation();
  return (
    <Navigate
      to={`${PRE_CANNED_REPORTS_ROUTE_PATH}/render/${encodeURIComponent(templateId)}${location.search}`}
      replace
    />
  );
};

// Main App Layout Component
const MainAppLayout: React.FC = () => {
  const headerHeight = '3.25rem';
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobileViewport, setIsMobileViewport] = useState(() => {
    if (typeof window === 'undefined') {
      return false;
    }

    return window.innerWidth < 1024;
  });
  // Desktop navigation starts collapsed so dashboard and chat surfaces get
  // the first-view width they need; users can still expand it from the rail.
  const [desktopSidebarCollapsed, setDesktopSidebarCollapsed] = useState(true);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleResize = () => {
      setIsMobileViewport(window.innerWidth < 1024);
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const getCurrentSection = () => {
    const path = location.pathname;
    if (path === '/') return 'finance-v2';
    if (path === SMART_INSIGHTS_ROUTE_PATH || path.startsWith(`${SMART_INSIGHTS_ROUTE_PATH}/`)) return 'finance-v2';
    if (path === SMART_DASHBOARDS_ROUTE_PATH || path.startsWith(`${SMART_DASHBOARDS_ROUTE_PATH}/`) || path === '/wow-dashboard' || path.startsWith('/wow-dashboard/')) return 'smart-dashboards';
    if (path === LABOR_PERFORMANCE_ROUTE_PATH || path.startsWith(`${LABOR_PERFORMANCE_ROUTE_PATH}/`) || path === `${SMART_MORNING_ROUTE_PATH}/labor-performance`) return 'labor-performance';
    if (path === SMART_MORNING_ROUTE_PATH || path.startsWith(`${SMART_MORNING_ROUTE_PATH}/`)) return 'smart-morning';
    if (path === SMART_MORNING_LEADERS_ROUTE_PATH || path.startsWith(`${SMART_MORNING_LEADERS_ROUTE_PATH}/`)) return 'pre-canned-reports';
    if (path === PRE_CANNED_REPORTS_ROUTE_PATH || path.startsWith(`${PRE_CANNED_REPORTS_ROUTE_PATH}/`)) return 'pre-canned-reports';
    if (path === LEGACY_PRE_CANNED_REPORTS_ROUTE_PATH || path.startsWith(`${LEGACY_PRE_CANNED_REPORTS_ROUTE_PATH}/`)) return 'pre-canned-reports';
    if (path === ALLOCATIONS_ROUTE_PATH || path.startsWith(`${ALLOCATIONS_ROUTE_PATH}/`)) return 'allocations';
    if (path === '/notifications') return 'notifications';
    if (path === '/users') return 'users';
    if (path === '/settings') return 'settings';
    if (path === REDZONE_ANALYSIS_ROUTE_PATH || path.startsWith(`${REDZONE_ANALYSIS_ROUTE_PATH}/`)) return 'redzone-analysis';
    return 'finance-v2';
  };

  const section = getCurrentSection();

  const handleSectionChange = async (section: string) => {
    // Navigate first, let individual sections handle their own data loading
    navigate(`/${section}`);
    
    // Remove automatic refresh on navigation to prevent excessive API calls
    // Individual sections will handle their own caching and data loading
    logInfo('App', `Navigated to section: ${section}`);
  };

  const handleSidebarNavigation = () => {
    if (isMobileViewport) {
      setMobileSidebarOpen(false);
      return;
    }
    // Desktop: auto-collapse the expanded sidebar after a navigation
    // click so the main content gets full width once the user has
    // picked a module. Already-collapsed state is a no-op.
    setDesktopSidebarCollapsed(true);
  };

  const handleSidebarToggle = () => {
    if (isMobileViewport) {
      setMobileSidebarOpen((current) => !current);
      return;
    }

    setDesktopSidebarCollapsed((current) => !current);
  };

  const isSidebarCollapsed = !isMobileViewport && desktopSidebarCollapsed;
  const isSidebarVisible = !isMobileViewport || mobileSidebarOpen;
  const isFullHeightShell = section === 'finance-v2' || section === 'smart-dashboards' || section === 'smart-morning';

  return (
    <div className="h-screen bg-secondary flex flex-col overflow-hidden">
      <div className="relative z-40 flex-shrink-0">
        <Header
          onToggleSidebar={handleSidebarToggle}
          isMobileViewport={isMobileViewport}
          isMobileSidebarOpen={mobileSidebarOpen}
        />
      </div>

      <div className={`relative flex flex-1 overflow-hidden ${isFullHeightShell ? 'min-h-0' : ''}`}>
        <div
          className={`fixed left-0 z-50 transition-[transform,width] duration-300 ease-in-out lg:static lg:z-auto lg:shrink-0 ${
            isSidebarVisible ? 'translate-x-0' : '-translate-x-full'
          }`}
          style={{
            top: isMobileViewport ? headerHeight : undefined,
            height: isMobileViewport ? `calc(100vh - ${headerHeight})` : '100%',
            width: isMobileViewport
              ? 'clamp(240px, 20vw, 256px)'
              : isSidebarCollapsed
                ? '4.375rem'
                : 'clamp(240px, 20vw, 256px)',
          }}
        >
          <Sidebar
            activeSection={section}
            onSectionChange={handleSectionChange}
            collapsed={isSidebarCollapsed}
            onNavigate={handleSidebarNavigation}
            onToggle={handleSidebarToggle}
            isMobileViewport={isMobileViewport}
          />
        </div>

        {/* Mobile overlay when sidebar is open */}
        {isMobileViewport && mobileSidebarOpen && (
          <div
            className="fixed inset-x-0 bottom-0 bg-black bg-opacity-50 z-40 lg:hidden"
            style={{ top: headerHeight }}
            onClick={() => setMobileSidebarOpen(false)}
          />
        )}

        <div className={`flex-1 flex flex-col min-w-0 overflow-hidden ${isFullHeightShell ? 'min-h-0' : ''}`}>
          <main className={isFullHeightShell ? 'flex-1 min-h-0 overflow-hidden' : 'flex-1 overflow-auto'}>
            <>
              {error && (
                <div className="mb-6 p-4 bg-error-50 border border-error-200 rounded-xl">
                  <div className="flex items-center">
                    <AlertTriangle className="w-5 h-5 text-error-400 mr-3" />
                    <p className="text-error-600">{error}</p>
                    <button
                      onClick={() => setError(null)}
                      className="ml-auto text-error-400 hover:text-error-600"
                    >
                      ×
                    </button>
                  </div>
                </div>
              )}

              <Routes>
              {/* Permission-backed module routes are wrapped in
                  <ModuleProtectedRoute> so direct URL access is blocked
                  for users who lack the matching `<slug>.access`.
                  Settings is route-accessible to all signed-in users so
                  Profile can be opened from the account menu; restricted
                  Settings tabs are gated inside SettingsSection.
                  module slug. */}
              <Route path={SMART_INSIGHTS_ROUTE_PATH} element={<ModuleProtectedRoute moduleSlug="smart_insights_bot"><FinanceBotV2Section /></ModuleProtectedRoute>} />
              <Route path={SMART_MORNING_LEADERS_ROUTE_PATH} element={<Navigate to={PRE_CANNED_REPORTS_ROUTE_PATH} replace />} />
              {PRE_CANNED_REPORTS_ENABLED && (
                <>
                  <Route path={`${PRE_CANNED_REPORTS_ROUTE_PATH}/render/:templateId`} element={<ProtectedRoute><PreCannedReportRenderPage /></ProtectedRoute>} />
                  <Route path={PRE_CANNED_REPORTS_ROUTE_PATH} element={<ProtectedRoute><PreCannedReportsPage /></ProtectedRoute>} />
                  <Route path={`${LEGACY_PRE_CANNED_REPORTS_ROUTE_PATH}/render/:templateId`} element={<LegacyPreCannedReportRenderRedirect />} />
                  <Route path={LEGACY_PRE_CANNED_REPORTS_ROUTE_PATH} element={<Navigate to={PRE_CANNED_REPORTS_ROUTE_PATH} replace />} />
                </>
              )}
              <Route path="/notifications" element={<ModuleProtectedRoute moduleSlug="notifications"><NotificationsSection /></ModuleProtectedRoute>} />
              <Route path="/users" element={<AdminProtectedRoute><UserManagementPage /></AdminProtectedRoute>} />
              <Route path="/adoption" element={<AdminProtectedRoute><AdoptionDashboardPage /></AdminProtectedRoute>} />
              <Route path="/settings" element={<SettingsSection />} />
              <Route path="/evaluations" element={<ProtectedRoute><EvaluationsSection /></ProtectedRoute>} />
              <Route path={REDZONE_ANALYSIS_ROUTE_PATH} element={<ModuleProtectedRoute moduleSlug="redzone_analysis"><RedzoneDowntimeAnalysisPage /></ModuleProtectedRoute>} />
              <Route path="/redzone-downtime" element={<Navigate to={REDZONE_ANALYSIS_ROUTE_PATH} replace />} />
              <Route path="/sap-intelligence" element={<ModuleProtectedRoute moduleSlug="sap_intelligence"><SapIntelligencePage /></ModuleProtectedRoute>} />
              <Route
                path={SMART_DASHBOARDS_ROUTE_PATH}
                element={
                  <ModuleProtectedRoute moduleSlug="sac_wow_dashboard">
                    <SacDashboardPage slug="wow-dashboard" fallbackTitle="SmartDashboards" />
                  </ModuleProtectedRoute>
                }
              />
              <Route path="/wow-dashboard" element={<Navigate to={SMART_DASHBOARDS_ROUTE_PATH} replace />} />
              <Route
                path={SMART_FINANCE_ROUTE_PATH}
                element={
                  <ModuleProtectedRoute moduleSlug="smart_finance">
                    <SacDashboardPage slug="smart-finance" fallbackTitle="SmartInsights V3" />
                  </ModuleProtectedRoute>
                }
              />
              <Route
                path={LABOR_PERFORMANCE_ROUTE_PATH}
                element={<ProtectedRoute><LaborPerformancePage /></ProtectedRoute>}
              />
              <Route
                path={SMART_MORNING_ROUTE_PATH}
                element={
                  <ModuleProtectedRoute moduleSlug="sac_smart_morning">
                    <SacDashboardPage slug="smart-morning" fallbackTitle="SmartMorning" />
                  </ModuleProtectedRoute>
                }
              />
              <Route
                path={`${SMART_MORNING_ROUTE_PATH}/labor-performance`}
                element={<Navigate to={LABOR_PERFORMANCE_ROUTE_PATH} replace />}
              />
              <Route
                path={ALLOCATIONS_ROUTE_PATH}
                element={
                  ENABLE_ALLOCATION_ACCESS_RESTRICTION ? (
                    <ModuleProtectedRoute moduleSlug="allocation">
                      <AllocationsSection />
                    </ModuleProtectedRoute>
                  ) : (
                    <AllocationsSection />
                  )
                }
              />

              {/* Root + unknown paths land on the first module the user can
                  access. */}
              <Route path="/" element={<RootRedirect />} />
              <Route path="*" element={<RootRedirect />} />
            </Routes>
            </>

          </main>
        </div>
      </div>
    </div>
  );
};

function App() {
  // Clear old authentication data on app start
  React.useEffect(() => {
    const wasCleared = clearOldAuthData();
    if (wasCleared) {
      console.log('🔄 Old authentication data cleared. Please log in again.');
    }
  }, []);

  return (
    <ErrorBoundary>
      <ThemeProvider>
        <Router basename="/">
          <AuthProvider>
            <AppWithToast />
          </AuthProvider>
        </Router>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

const AppWithToast: React.FC = () => {
  return (
    <ToastProvider>
      <Routes>
        <Route path="/login" element={<LoginForm />} />
        <Route path="/callback" element={<LoginCallback />} />
        {/* SAP OAuth callback — SAP redirects here with ?code=...&state=... */}
        <Route path="/sap/callback" element={<SapCallback />} />
        <Route path="/change-password" element={<ChangePasswordForm />} />
        <Route path="/*" element={
          <ProtectedRoute>
            <MainAppLayout />
          </ProtectedRoute>
        } />
      </Routes>
    </ToastProvider>
  );
};

export default App;
